import { InlineFeedback } from '../../feedback/index.js';
import { useEffect, useRef, useState } from 'react';
import { contentEditorSelectionLocks } from './contentEditorSelectionLocks.js';
import { moveContentBlockTo, contentBlockCanAppend } from './contentEditorCommands.js';
import { ContentEditorBlockMenu } from './ContentEditorBlockMenu.jsx';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { contentEditorBlockIcon, contentEditorBlockSummary } from './contentEditorBlockPresentation.js';
import { CONTENT_EDITOR_HOST_LOAD_META } from './useContentEditorKernel.js';
const OUTLINE_DRAG_TYPE = 'application/x-mts-content-outline-block';

export function useContentEditorOutlineState(editor, tenantId, profileId) {
  const collapsed = useRef(new Set());
  useEffect(() => {
    collapsed.current.clear();
    if (!editor) return;
    const update = ({ transaction }) => {
      if (transaction.getMeta(CONTENT_EDITOR_HOST_LOAD_META)) collapsed.current.clear();
      else if (transaction.docChanged) collapsed.current = new Set(
        [...collapsed.current].map(position => transaction.mapping.mapResult(position, 1))
          .filter(mapped => !mapped.deleted).map(mapped => mapped.pos)
      );
    };
    editor.on('beforeTransaction', update);
    return () => editor.off('beforeTransaction', update);
  }, [editor, tenantId, profileId]);
  return collapsed;
}

export function ContentEditorOutline({ editor, profile, onSelect, tenantId, collapseState }) {
  const [items, setItems] = useState([]);
  const source = useRef(null);
  const [dragging, setDragging] = useState(false);
  const localCollapseState = useContentEditorOutlineState(collapseState ? null : editor, tenantId, profile.id);
  const collapsed = collapseState || localCollapseState;
  const [, redraw] = useState(0);
  const [selected, setSelected] = useState(null);
  const toggle = (position) => { if (collapsed.current.has(position)) collapsed.current.delete(position); else collapsed.current.add(position); redraw(value => value + 1); };
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (!editor) return;
    const update = ({ transaction } = {}) => {
      if (transaction?.getMeta(CONTENT_EDITOR_HOST_LOAD_META)) { collapsed.current.clear(); source.current = null; setDragging(false); setMessage(''); }
      const next = [];
      const walk = (parent, offset, depth) => parent.forEach((node, index) => {
        const position = offset + index;
        if (!node.isBlock) return;
        const item = { node, position, depth, type: node.type.name, label: node.textContent.slice(0, 60) || node.attrs.title || node.type.name, summary: contentEditorBlockSummary(node), children: [] };
        next.push(item);
        const start = next.length;
        if (!node.isLeaf) walk(node, position + 1, depth + 1);
        item.children = next.slice(start).filter(child => child.depth === depth + 1);
      });
      walk(editor.state.doc, 0, 1);
      const selection = editor.state.selection;
      const active = selection.node ? selection.from : selection.$from.depth ? selection.$from.before(selection.$from.depth) : null;
      setSelected(active);
      for (const item of next) if (transaction && item.position < active && item.position + item.node.nodeSize > active) collapsed.current.delete(item.position);
      setItems(next);
    };
    update(); editor.on('transaction', update);
    return () => editor.off('transaction', update);
  }, [editor]);
  const dragOver = (event) => {
    if (source.current && event.dataTransfer.types.includes(OUTLINE_DRAG_TYPE)) event.preventDefault();
  };
  const drop = (event, target) => {
    if (!source.current || !event.dataTransfer.types.includes(OUTLINE_DRAG_TYPE)) return;
    event.preventDefault();
    const request = source.current;
    source.current = null;
    setDragging(false);
    if (request.document !== editor.state.doc) { setMessage('The document changed during dragging. Try again.'); return; }
    setMessage(moveContentBlockTo(editor, request.position, target) ? 'Block moved.' : 'This block cannot be moved here.');
  };
  const renderItem = (item) => {
    const locks = contentEditorSelectionLocks(editor, item, profile);
    const expanded = !collapsed.current.has(item.position);
    const draggedNode = source.current?.document.nodeAt(source.current.position);
    const canDropInside = dragging && draggedNode && profile.blocks.find(block => block.type === draggedNode.type.name)?.allowedParents.includes(item.type) && contentBlockCanAppend(item.node, draggedNode) && !(item.position >= source.current.position && item.position < source.current.position + draggedNode.nodeSize);
    const label = profile.blocks.find(block => block.type === item.type)?.label || item.type.replace(/([A-Z])/g, ' $1');
    const Icon = contentEditorBlockIcon(item.type, profile.blocks.find(block => block.type === item.type)?.category);
    return <li key={item.position} role="treeitem" aria-level={item.depth} aria-selected={item.position === selected} aria-expanded={item.children.length ? expanded : undefined}>
      {dragging ? <button type="button" className="mts-editor-outline-drop-target" aria-label={`Move block before ${item.type}: ${item.label}`} disabled={!editor?.isEditable} onDragOver={dragOver} onDrop={event => drop(event, item.position)}>Drop before {label}</button> : null}
      <div className="mts-editor-outline-row" data-selected={item.position === selected}>
        <button type="button" aria-label={`${item.type} ${item.label}`} draggable={Boolean(editor?.isEditable && !locks.move)} onDragStart={(event) => {
          if (!editor?.isEditable || contentEditorSelectionLocks(editor, item, profile).move) { event.preventDefault(); return; }
          source.current = { position: item.position, document: editor.state.doc };
          setDragging(true);
          event.dataTransfer.setData(OUTLINE_DRAG_TYPE, 'move'); event.dataTransfer.effectAllowed = 'move';
        }} onDragEnd={() => { source.current = null; setDragging(false); }} onDragOver={dragOver} onDrop={event => drop(event, item.position)} onClick={() => { onSelect(item.position); editor.commands.scrollIntoView(); }}
        onKeyDown={event => {
          const rows = [...event.currentTarget.closest('[role=tree]').querySelectorAll('.mts-editor-outline-select')];
          const index = rows.indexOf(event.currentTarget);
          if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); rows[event.key === 'Home' ? 0 : rows.length - 1]?.focus(); }
          else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); rows[Math.max(0, Math.min(rows.length-1, index + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus(); }
          else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            if (item.children.length && expanded) toggle(item.position);
            else event.currentTarget.closest('[role=treeitem]')?.parentElement?.closest('[role=treeitem]')?.querySelector('.mts-editor-outline-select')?.focus();
          } else if (event.key === 'ArrowRight' && item.children.length) {
            event.preventDefault();
            if (!expanded) toggle(item.position);
            else event.currentTarget.closest('[role=treeitem]')?.querySelector('[role=group] .mts-editor-outline-select')?.focus();
          }
        }} className="mts-editor-outline-select"><Icon size={16} aria-hidden="true"/><span><strong>{label}</strong><small>{item.summary || label}</small></span>{locks.edit || locks.move || locks.remove ? <Lock size={13} aria-label="Protected block"/> : null}</button>
        <ContentEditorBlockMenu editor={editor} profile={profile} selection={item} tenantId={tenantId} label={`Actions for ${label}: ${item.label}`}/>
        {item.children.length ? <button type="button" aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`} aria-expanded={expanded} onClick={() => toggle(item.position)}>{expanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}</button> : null}
      </div>
      {canDropInside ? <button hidden={!dragging} type="button" className="mts-editor-outline-drop-target" aria-label={`Move block into ${item.type}: ${item.label}`} disabled={!editor?.isEditable || locks.edit} onDragOver={dragOver} onDrop={event => drop(event, item.position + item.node.nodeSize - 1)}>Drop inside {label}</button> : null}
      {expanded && item.children.length ? <ul role="group">{item.children.map(renderItem)}</ul> : null}
      <button hidden={!dragging} type="button" className="mts-editor-outline-drop-target" aria-label={`Move block after ${item.type}: ${item.label}`} disabled={!editor?.isEditable} onDragOver={dragOver} onDrop={event => drop(event, item.position + item.node.nodeSize)}>Drop after {label}</button>
    </li>;
  };
  return <nav aria-label="Document outline" className="mts-editor-panel"><h2>Outline</h2><ul role="tree" aria-label="Document blocks" className="mts-editor-outline-tree">{items.filter(item => item.depth === 1).map(renderItem)}</ul>{!items.length ? <p>No blocks in this document.</p> : null}{message ? <InlineFeedback variant="status" role="status">{message}</InlineFeedback> : null}</nav>;
}
